import { useEffect, useRef, useState } from 'react';

/**
 * PulseNumber — renders a numeric value and briefly flashes when it changes.
 *
 *   <PulseNumber value={grand.deposits_usd} format={fmtUSD} />
 *
 * Direction is detected automatically:
 *   value went UP   → green flash
 *   value went DOWN → amber flash
 *   (first render never flashes — we need a baseline)
 *
 * This is the "trading-floor" feel on the admin analytics dashboard: the
 * moment a deposit clears, the number glows without any explicit animation
 * on the caller's side.
 */
export default function PulseNumber({
  value,
  format = (v) => v,
  duration = 900,
  className = '',
  'data-testid': dataTestId,
}) {
  const prevRef = useRef(value);
  const [pulse, setPulse] = useState(null); // 'up' | 'down' | null

  useEffect(() => {
    const prev = prevRef.current;
    // Compare numerically; skip string values (they're formatted already).
    const a = Number(prev);
    const b = Number(value);
    if (!Number.isNaN(a) && !Number.isNaN(b) && a !== b) {
      setPulse(b > a ? 'up' : 'down');
      const t = setTimeout(() => setPulse(null), duration);
      prevRef.current = value;
      return () => clearTimeout(t);
    }
    prevRef.current = value;
    return undefined;
  }, [value, duration]);

  const flash =
    pulse === 'up'
      ? 'bg-green-400/25 ring-1 ring-green-400/50 text-green-200'
      : pulse === 'down'
        ? 'bg-amber-400/20 ring-1 ring-amber-400/40 text-amber-200'
        : '';

  return (
    <span
      className={`inline-block px-1.5 -mx-1.5 rounded transition-colors duration-500 ${flash} ${className}`}
      data-testid={dataTestId}
    >
      {format(value)}
    </span>
  );
}
